import type { IanaTimeZone, SleepKind, UtcInstant } from '@baby-tracker/validation';
import { useState, type ReactElement } from 'react';

import { familyDateTimeToUtc } from '../../../lib/family-time.js';
import { Button } from '../../ui/Button.js';
import { FormError } from '../../ui/FormError.js';
import { SegmentedControl } from '../../ui/SegmentedControl.js';
import { Sheet } from '../../ui/Sheet.js';
import fieldStyles from '../../ui/fields.module.css';
import { TextField } from '../../ui/TextField.js';
import styles from './tracking.module.css';

const kindOptions = [
    { value: 'nap', label: 'Nap' },
    { value: 'night', label: 'Night' },
    { value: 'unspecified', label: 'Sleep' },
] as const satisfies readonly { value: SleepKind; label: string }[];

export function SleepSheet({
    open,
    suggestion,
    timeZone,
    pending,
    error,
    onClose,
    onStart,
    onComplete,
}: {
    open: boolean;
    suggestion: 'nap' | 'night';
    timeZone: IanaTimeZone;
    pending: boolean;
    error: string | null;
    onClose: () => void;
    onStart: (kind: SleepKind) => Promise<void>;
    onComplete: (values: {
        kind: SleepKind;
        startAt: UtcInstant;
        endAt: UtcInstant;
    }) => Promise<void>;
}): ReactElement {
    const [mode, setMode] = useState<'timer' | 'times'>('timer');
    const [kind, setKind] = useState<SleepKind>(suggestion);
    const [started, setStarted] = useState('');
    const [ended, setEnded] = useState('');
    const [timeError, setTimeError] = useState<string | undefined>();

    return (
        <Sheet title="Sleep" open={open} onClose={onClose}>
            <form
                className={fieldStyles.stack}
                noValidate
                onSubmit={(event) => {
                    event.preventDefault();
                    if (mode === 'timer') {
                        void onStart(kind);
                        return;
                    }
                    const startAt = familyDateTimeToUtc(started, timeZone);
                    const endAt = familyDateTimeToUtc(ended, timeZone);
                    if (startAt === undefined || endAt === undefined) {
                        setTimeError('Enter a start and an end.');
                        return;
                    }
                    const duration = Date.parse(endAt) - Date.parse(startAt);
                    if (duration <= 0) {
                        setTimeError('Choose an end time after the start.');
                        return;
                    }
                    if (duration > 24 * 60 * 60 * 1000) {
                        setTimeError('A logged sleep can be up to 24 hours.');
                        return;
                    }
                    setTimeError(undefined);
                    void onComplete({ kind, startAt, endAt });
                }}
            >
                <SegmentedControl
                    label="How to log"
                    value={mode}
                    options={[
                        { value: 'timer', label: 'Timer' },
                        { value: 'times', label: 'Enter times' },
                    ]}
                    onChange={setMode}
                />
                <SegmentedControl
                    label="Kind"
                    value={kind}
                    options={kindOptions}
                    onChange={setKind}
                />
                {mode === 'times' ? (
                    <>
                        <TextField
                            label="Start"
                            type="datetime-local"
                            value={started}
                            onChange={(event) => {
                                setStarted(event.target.value);
                            }}
                            required
                        />
                        <TextField
                            label="End"
                            type="datetime-local"
                            value={ended}
                            onChange={(event) => {
                                setEnded(event.target.value);
                            }}
                            required
                        />
                        <p className={styles.meta}>Times use the family time zone.</p>
                    </>
                ) : null}
                <FormError message={timeError ?? error} />
                <Button type="submit" loading={pending}>
                    {mode === 'times' ? 'Save sleep' : 'Start sleep'}
                </Button>
            </form>
        </Sheet>
    );
}
